'use client';

import { skipToken } from '@reduxjs/toolkit/query';

import { useGetJobDeliverablesQuery } from '@/features/jobs/api/jobsApi';
import { SmartInput } from '@web/ui';

import type { JobDeliverable } from '@/features/jobs/types';

type JobDeliverableSelectFieldProps = {
  jobId: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  name?: string;
  error?: string;
  disabled?: boolean;
  emptyLabel?: string;
};

const normalizeDeliverables = (raw: unknown): JobDeliverable[] => {
  if (Array.isArray(raw)) return raw as JobDeliverable[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).results)) {
    return (raw as any).results as JobDeliverable[];
  }
  return [];
};

export function JobDeliverableSelectField({
  jobId,
  value,
  onChange,
  label = 'Deliverable',
  name = 'deliverable-select',
  error,
  disabled = false,
  emptyLabel = 'Select deliverable',
}: JobDeliverableSelectFieldProps) {
  const { data, isFetching } = useGetJobDeliverablesQuery(
    jobId
      ? {
          jobId,
          params: {
            page_size: 100,
          },
        }
      : skipToken,
  );

  const deliverables = normalizeDeliverables(data?.data);
  const options = [
    { value: '', label: emptyLabel },
    ...deliverables.map((deliverable) => ({
      value: deliverable.id,
      label: deliverable.name,
    })),
  ];

  return (
    <SmartInput
      type="select"
      name={name}
      label={label}
      value={value}
      options={options}
      readOnly={disabled || !jobId || isFetching}
      error={error}
      onChange={(nextValue) => onChange(String(nextValue))}
    />
  );
}
